import React from "react";
import { useTranslation } from "react-i18next";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import LogoMonogram from "@/components/LogoMonogram";
import { usePWAInstall } from "@/contexts/PWAInstallContext";

/**
 * Floating bottom-right banner that nudges the user to install the PWA when
 * the browser fires `beforeinstallprompt`. Dismissal is persisted for 7 days
 * via the PWAInstallContext.
 */
export default function PWAInstallBanner() {
  const { t } = useTranslation();
  const { shouldShowBanner, install, dismissBanner } = usePWAInstall();

  if (!shouldShowBanner) return null;

  const onInstall = async () => {
    const accepted = await install();
    // Whether accepted or not, hide the banner so it stops nagging the user
    // within the dismissal window.
    if (!accepted) dismissBanner();
  };

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 print:hidden sm:inset-x-auto sm:bottom-6 sm:right-6 sm:left-auto sm:justify-end"
      data-testid="pwa-install-banner"
    >
      <div className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border border-slate-700/70 bg-slate-900/95 p-3 text-slate-100 shadow-2xl backdrop-blur-md sm:p-4">
        <div className="hidden shrink-0 sm:block">
          <LogoMonogram size={40} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            {t("share.app.bannerTitle")}
          </div>
          <div className="truncate text-xs text-slate-400">
            {t("share.app.bannerSubtitle")}
          </div>
        </div>
        <Button
          size="sm"
          onClick={onInstall}
          data-testid="pwa-install-accept"
          className="gap-1.5"
        >
          <Download className="h-3.5 w-3.5" />
          {t("share.app.install")}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={dismissBanner}
          data-testid="pwa-install-dismiss"
          aria-label={t("common.cancel")}
          className="h-8 w-8 text-slate-400 hover:text-slate-100"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
